// 任务管理 Hook - useTask.ts
// 管理任务状态和相关操作

import { ref, reactive, Ref } from 'vue';
import { getJSON } from '@/api/request';
import { getActivityApi } from '@/utils';
import { taskText } from '@/config';

// 类型定义
interface TaskItem {
  key: string;
  subTaskId?: string;
  levelId?: string; // 组队任务的 levelId
  title: string;
  descript: string;
  completed: boolean;
  disabled: boolean;
  progress?: number;
  maxProgress?: number;
  score?: number;
  redirectUrl?: string;
  login?: boolean;
  callbackKey?: number;
  feedbackPopup?: boolean;
  ugcTab?: number;
  autoOpen?: boolean;
  centerTab?: boolean;
  hideGoButton?: boolean; // 隐藏"去完成"按钮
  taskKey?: string; // 从接口返回的任务key
  status?: string; // 任务状态: "0"未完成, "1"已完成, "2"已领取
  children?: TaskItem[]; // 子任务（组队任务）
}

interface TaskConfig {
  platformId: string;
  activityName: string;
}

export function useTask(config: TaskConfig) {
  const { platformId, activityName } = config;

  // ========== 状态管理 ==========

  // 任务列表
  const taskList = ref<TaskItem[]>(
    taskText.map(task => ({
      ...task,
      completed: false,
      disabled: false,
      progress: 0,
      maxProgress: 100,
      score: 0,
    }))
  );

  // 总积分
  const totalScore = ref(0);

  // ========== 核心功能函数 ==========

  /**
   * 初始化任务列表
   */
  const initTaskList = async () => {
    try {
      // 真实接口 - 使用新的 refreshTaskCenter action
      const res = await getJSON(
        getActivityApi(platformId, activityName, 'refreshTaskCenter'),
        {}
      );

      if (res.error === 0 && res.data?.taskList) {
        // 解析新接口返回的任务列表
        const apiTaskList = res.data.taskList;

        taskList.value.forEach(task => {
          // 查找对应的接口任务数据
          const apiTask = apiTaskList.find((t: any) => t.taskId === task.key);

          if (apiTask && apiTask.subTaskList && apiTask.subTaskList.length > 0) {
            const subTask = apiTask.subTaskList[0]; // 获取第一个子任务

            if (task.children && subTask.levelConfigList) {
              // 处理组队任务（有 children 的任务）
              task.children = task.children.map(child => {
                const levelConfig = subTask.levelConfigList.find(
                  (lc: any) => lc.levelId === child.levelId
                );

                if (levelConfig) {
                  return {
                    ...child,
                    status: levelConfig.status,
                    taskKey: levelConfig.taskKey,
                    completed: levelConfig.status === '1' || levelConfig.status === '2',
                    disabled: levelConfig.status === '2',
                  };
                }
                return child;
              });

              // 父任务状态：所有子任务都完成才算完成
              task.completed = task.children.every(c => c.completed);
            } else if (subTask.levelConfigList && subTask.levelConfigList.length > 0) {
              // 处理普通任务（没有 children 的任务）
              const levelConfig = subTask.levelConfigList[0];
              task.status = levelConfig.status;
              task.taskKey = levelConfig.taskKey;
              task.completed = levelConfig.status === '1' || levelConfig.status === '2';
              task.disabled = levelConfig.status === '2';
            }
          }
        });
      }
    } catch (error) {
      console.error('获取任务列表失败:', error);
    }
  };

  /**
   * 完成任务
   */
  const completeTask = async (taskKey: string, params?: any) => {
    try {
      // 真实接口
      const res = await getJSON(
        getActivityApi(platformId, activityName, 'completeTask'),
        {
          params: JSON.stringify({
            taskKey,
            ...params,
          }),
        }
      );

      if (res.result === 1) {
        // 更新任务状态
        const task = taskList.value.find(t => t.key === taskKey);
        if (task) {
          task.completed = true;
          task.progress = task.maxProgress || 100;
        }

        // 更新总积分
        if (res.data?.totalScore !== undefined) {
          totalScore.value = res.data.totalScore;
        }

        return {
          success: true,
          data: res.data,
          msg: res.msg || '任务完成',
        };
      } else {
        return {
          success: false,
          code: res.code,
          msg: res.msg || '任务完成失败',
        };
      }
    } catch (error) {
      console.error('完成任务失败:', error);
      return {
        success: false,
        msg: '网络错误',
      };
    }
  };

  /**
   * 更新任务状态
   */
  const updateTaskStatus = (taskKey: string, status: Partial<TaskItem>) => {
    const task = taskList.value.find(t => t.key === taskKey);
    if (task) {
      Object.assign(task, status);
    }
  };

  /**
   * 获取任务进度文本
   */
  const getTaskProgressText = (task: TaskItem): string => {
    if (!task.descript) return '';

    // 替换 {n} 占位符为实际进度
    if (task.descript.includes('{n}')) {
      return task.descript.replace('{n}', String(task.progress || 0));
    }

    return task.descript;
  };

  /**
   * 检查任务是否可执行
   */
  const canExecuteTask = (taskKey: string): boolean => {
    const task = taskList.value.find(t => t.key === taskKey);
    if (!task) return false;

    return !task.completed && !task.disabled;
  };

  /**
   * 获取已完成任务数量
   */
  const getCompletedTaskCount = (): number => {
    return taskList.value.filter(t => t.completed).length;
  };

  /**
   * 获取总任务数量
   */
  const getTotalTaskCount = (): number => {
    return taskList.value.length;
  };

  /**
   * 刷新任务列表
   */
  const refreshTaskList = async () => {
    await initTaskList();
  };

  /**
   * 领取任务奖励
   * @param taskKey - 从接口返回的 taskKey
   * @param refreshBasicFn - 刷新 basic 接口的函数（用于更新积分）
   */
  const claimTaskReward = async (
    taskKey: string,
    refreshBasicFn?: () => Promise<void>
  ) => {
    try {
      // 调用领取奖励接口
      const res = await getJSON(
        getActivityApi(platformId, activityName, 'claimTaskCenterReward'),
        {
          params: JSON.stringify({
            taskKey: parseInt(taskKey), // 转为 int 类型
          }),
        }
      );

      if (res.result === 1) {
        // 手动更新任务状态为已完成
        // 遍历所有任务，找到匹配的 taskKey
        taskList.value.forEach(task => {
          if (task.children) {
            // 组队任务：检查子任务
            task.children.forEach(child => {
              if (child.taskKey === taskKey) {
                child.status = '2'; // 已领取
                child.completed = true;
                child.disabled = true;
              }
            });
            // 更新父任务状态
            task.completed = task.children.every(c => c.completed);
          } else {
            // 普通任务
            if (task.taskKey === taskKey) {
              task.status = '2'; // 已领取
              task.completed = true;
              task.disabled = true;
            }
          }
        });

        // 刷新 basic 接口更新积分
        if (refreshBasicFn) {
          await refreshBasicFn();
        }

        return {
          success: true,
          msg: '領取成功',
        };
      } else {
        return {
          success: false,
          code: res.code,
          msg: res.msg || '領取失敗',
        };
      }
    } catch (error) {
      console.error('领取任务奖励失败:', error);
      return {
        success: false,
        msg: '網絡錯誤',
      };
    }
  };

  // ========== 返回值 ==========
  return {
    // 状态
    taskList,
    totalScore,

    // 方法
    initTaskList,
    completeTask,
    updateTaskStatus,
    getTaskProgressText,
    canExecuteTask,
    getCompletedTaskCount,
    getTotalTaskCount,
    refreshTaskList,
    claimTaskReward, // 新增领取奖励方法
  };
}
